import { useMemo } from 'react';
import { useProjectEntitlements, PRODUCT_IDS } from '@adsk/alloy-provider';
import { PRODUCT_STATUSES } from '@adsk/acs-admin-ui-components-helpers';
import useGetProjectProducts from './useGetProjectProducts';
import useGetAccountOfferings from './useGetAccountOfferings';

const isAccountAdminOrIncluded = (isAccountAdmin, product) =>
  isAccountAdmin || (!isAccountAdmin && product.included);

const isAssignableOrInsight = (product) =>
  product.userAssignable || product.type === PRODUCT_IDS.INSIGHT;

const useAssignableProjectProducts = (enable = true) => {
  const { projectProducts, isLoadingProjectData } =
    useGetProjectProducts(enable);
  const { offeringProducts, isLoadingOfferingProducts } =
    useGetAccountOfferings(enable);
  const { currentProject } = useProjectEntitlements();
  const isAccountAdmin = currentProject?.accessLevels?.accountAdministrator;

  const assignableProducts = useMemo(() => {
    if (!projectProducts || !offeringProducts || !enable) return [];

    return projectProducts
      .filter(
        (product) =>
          (product.status === PRODUCT_STATUSES.ACTIVE ||
            product.status === PRODUCT_STATUSES.ACTIVATING) &&
          offeringProducts.some(
            (offeringProduct) =>
              product.key === offeringProduct.type &&
              isAccountAdminOrIncluded(isAccountAdmin, offeringProduct) &&
              isAssignableOrInsight(offeringProduct)
          )
      )
      .map((product) => {
        const offeringProduct = offeringProducts.find(
          (op) => product.key === op.type
        );
        return {
          ...product,
          included: offeringProduct?.included ?? false,
          type: product.key,
          userAssignable: offeringProduct?.userAssignable ?? false,
        };
      })
      .sort(({ name: a }, { name: b }) => a.localeCompare(b));
  }, [enable, projectProducts, offeringProducts, isAccountAdmin]);

  const productsMapByKey = useMemo(
    () =>
      assignableProducts.reduce(
        (map, product) => ({ ...map, [product.key]: product }),
        {}
      ),
    [assignableProducts]
  );

  return {
    data: assignableProducts,
    productsMapByKey,
    loading: isLoadingProjectData || isLoadingOfferingProducts,
  };
};

export default useAssignableProjectProducts;
